import { useState, useEffect } from 'react';
import { api } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { VibeMatch } from '../types/index.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Sparkles, Heart, X, User, Music, Zap, Users, Trophy, 
  TrendingUp, Star, Flame, Crown, Target, Award, 
  PartyPopper, Smile, Meh, Frown, Coffee, Moon, Sun
} from 'lucide-react';
import { toast } from 'sonner';

interface VibeCheckHistory {
  vibe_id: string;
  score: number;
  mood: string;
  analysis: string;
  created_at: string;
}

interface VibeStats {
  total_checks: number;
  average_score: number;
  highest_score: number;
  current_streak: number;
  total_matches: number;
  accepted_matches: number;
}

const MOOD_OPTIONS = [
  { emoji: '🔥', label: 'On Fire', value: 'on_fire', color: 'bg-orange-500' },
  { emoji: '😄', label: 'Excited', value: 'excited', color: 'bg-yellow-500' },
  { emoji: '😎', label: 'Chill', value: 'chill', color: 'bg-blue-500' },
  { emoji: '🎉', label: 'Party Mode', value: 'party', color: 'bg-pink-500' },
  { emoji: '😌', label: 'Relaxed', value: 'relaxed', color: 'bg-green-500' },
  { emoji: '🤔', label: 'Curious', value: 'curious', color: 'bg-purple-500' },
];

const VIBE_BADGES = [
  { id: 'first_check', name: 'First Vibe', icon: Star, requirement: 'Complete your first vibe check', unlocked: true },
  { id: 'high_scorer', name: 'High Scorer', icon: Trophy, requirement: 'Score 90+ on a vibe check', unlocked: false },
  { id: 'social_butterfly', name: 'Social Butterfly', icon: Users, requirement: 'Get 10 vibe matches', unlocked: false },
  { id: 'streak_master', name: 'Streak Master', icon: Flame, requirement: '7-day vibe check streak', unlocked: false },
  { id: 'vibe_king', name: 'Vibe Royalty', icon: Crown, requirement: 'Reach 1000 total vibe score', unlocked: false },
];

export default function Vibe() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('check');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [matches, setMatches] = useState<VibeMatch[]>([]);
  const [vibeHistory, setVibeHistory] = useState<VibeCheckHistory[]>([]);
  const [vibeStats, setVibeStats] = useState<VibeStats>({
    total_checks: 0,
    average_score: 0,
    highest_score: 0,
    current_streak: 0,
    total_matches: 0,
    accepted_matches: 0
  });
  
  // Vibe Check Form State
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [energyLevel, setEnergyLevel] = useState(50);
  const [socialVibe, setSocialVibe] = useState(50);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [currentVibeScore, setCurrentVibeScore] = useState<number | null>(null);
  const [vibeAnalysis, setVibeAnalysis] = useState<string | null>(null);

  const genres = ['EDM', 'Hip Hop', 'Rock', 'Pop', 'Jazz', 'R&B', 'Indie', 'Latin', 'Afrobeats', 'House'];

  useEffect(() => {
    loadVibeData();
  }, []);

  const loadVibeData = async () => {
    try {
      const [matchesData, checksData] = await Promise.all([
        api.getVibeMatches(),
        api.getVibeChecks()
      ]);
      setMatches(matchesData || []);
      setVibeHistory(checksData || []);
      
      // Calculate stats
      if (checksData && checksData.length > 0) {
        const scores = checksData.map((c: VibeCheckHistory) => c.score);
        setVibeStats({
          total_checks: checksData.length,
          average_score: Math.round(scores.reduce((a: number, b: number) => a + b, 0) / scores.length),
          highest_score: Math.max(...scores),
          current_streak: calculateStreak(checksData),
          total_matches: matchesData?.length || 0,
          accepted_matches: matchesData?.filter((m: VibeMatch) => m.status === 'accepted').length || 0
        });
      }
    } catch (error) {
      console.error('Failed to load vibe data:', error);
    }
  };

  const calculateStreak = (checks: VibeCheckHistory[]) => {
    // Simplified streak calculation
    return Math.min(checks.length, 7);
  };

  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  const handleSubmitVibe = async () => {
    if (!selectedMood) {
      toast.error('Please select your current mood');
      return;
    }

    try {
      setIsSubmitting(true);
      
      const response = await api.submitVibeCheck({
        mood: selectedMood,
        energy_level: energyLevel,
        social_preference: socialVibe,
        music_taste: selectedGenres,
      });
      
      setCurrentVibeScore(response.score || Math.round((energyLevel + socialVibe) / 2 + Math.random() * 20));
      setVibeAnalysis(response.analysis || getVibeAnalysis(selectedMood, energyLevel));
      
      toast.success('Vibe check complete! 🎉');
      await loadVibeData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit vibe check');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getVibeAnalysis = (mood: string, energy: number) => {
    const analyses: Record<string, string[]> = {
      on_fire: ["You're absolutely radiating! People are drawn to your energy!", "Unstoppable vibes! Time to make memories!"],
      excited: ["Your excitement is contagious! Perfect for meeting new people!", "Ready to have an amazing time!"],
      chill: ["Cool and collected - your calm energy attracts great connections!", "Smooth vibes, perfect for deep conversations!"],
      party: ["Party animal mode activated! You're the life of the event!", "Ready to dance the night away!"],
      relaxed: ["Zen mode engaged - attracting peaceful connections!", "Your relaxed energy is a vibe magnet!"],
      curious: ["Open to new experiences - expect unexpected connections!", "Adventure awaits with your curious spirit!"],
    };
    const moodAnalyses = analyses[mood] || ["Great vibes detected!"];
    return moodAnalyses[Math.floor(Math.random() * moodAnalyses.length)];
  };

  const handleMatchResponse = async (matchId: string, status: 'accepted' | 'rejected') => {
    try {
      await api.respondToMatch(matchId, status);
      toast.success(status === 'accepted' ? '🎉 Match accepted!' : 'Match passed');
      setMatches(prev => prev.filter(m => m.match_id !== matchId));
      await loadVibeData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to respond to match');
    }
  };

  const getEnergyLabel = (value: number) => {
    if (value < 25) return { label: 'Low Key', icon: Moon };
    if (value < 50) return { label: 'Warming Up', icon: Coffee };
    if (value < 75) return { label: 'Energized', icon: Sun };
    return { label: 'Maximum', icon: Zap };
  };

  const getSocialLabel = (value: number) => {
    if (value < 25) return 'Solo Vibes';
    if (value < 50) return 'Small Squad';
    if (value < 75) return 'Social Mode';
    return 'Party Animal';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const energyInfo = getEnergyLabel(energyLevel);
  const EnergyIcon = energyInfo.icon;

  return (
    <div className="space-y-6 pb-20 md:pb-8">
      {/* Header with Vibe Score */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <Sparkles className="h-10 w-10 text-primary animate-pulse" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              Vibe Zone
            </h1>
          </div>
          <p className="text-lg text-gray-400">
            Check your vibe, find your tribe
          </p>
        </div>
        
        {/* Personal Vibe Score Card */}
        <Card className="glass border-white/10 p-4 min-w-[200px]">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <span className="text-2xl font-bold text-white">{vibeStats.average_score || '?'}</span>
              </div>
              {vibeStats.current_streak > 0 && (
                <div className="absolute -top-1 -right-1 h-6 w-6 bg-orange-500 rounded-full flex items-center justify-center">
                  <Flame className="h-4 w-4 text-white" />
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-400">Your Vibe Score</p>
              <p className="font-semibold">{vibeStats.current_streak} day streak 🔥</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-background-subtle">
          <TabsTrigger value="check" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            <span className="hidden sm:inline">Vibe Check</span>
          </TabsTrigger>
          <TabsTrigger value="matches" className="flex items-center gap-2">
            <Heart className="h-4 w-4" />
            <span className="hidden sm:inline">Matches</span>
            {matches.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {matches.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Stats</span>
          </TabsTrigger>
          <TabsTrigger value="badges" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            <span className="hidden sm:inline">Badges</span>
          </TabsTrigger>
        </TabsList>

        {/* Vibe Check Tab */}
        <TabsContent value="check" className="space-y-6 mt-6">
          {currentVibeScore !== null ? (
            // Show Results
            <Card className="glass border-white/10 overflow-hidden">
              <div className="bg-gradient-to-br from-primary/20 to-secondary/20 p-8 text-center space-y-6">
                <PartyPopper className="h-16 w-16 mx-auto text-primary animate-bounce" />
                <div>
                  <h2 className="text-2xl font-heading font-bold mb-2">Your Vibe Score</h2>
                  <div className="relative inline-block">
                    <div className={`text-7xl font-bold ${getScoreColor(currentVibeScore)}`}>
                      {currentVibeScore}
                    </div>
                    <Sparkles className="absolute -top-2 -right-4 h-8 w-8 text-yellow-400 animate-pulse" />
                  </div>
                </div>
                <p className="text-lg text-gray-300 max-w-md mx-auto">{vibeAnalysis}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                  <Button onClick={() => { setCurrentVibeScore(null); setVibeAnalysis(null); }} variant="outline" className="border-white/20">
                    New Vibe Check
                  </Button>
                  <Button onClick={() => setActiveTab('matches')} className="shadow-glow">
                    <Users className="h-4 w-4 mr-2" />
                    Find Matches
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            // Show Form
            <div className="space-y-6">
              {/* Mood Selection */}
              <Card className="glass border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smile className="h-5 w-5 text-primary" />
                    How are you feeling right now?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {MOOD_OPTIONS.map((mood) => (
                      <button
                        key={mood.value}
                        onClick={() => setSelectedMood(mood.value)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          selectedMood === mood.value
                            ? 'border-primary bg-primary/20 scale-105'
                            : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                        }`}
                      >
                        <span className="text-3xl block mb-2">{mood.emoji}</span>
                        <span className="text-sm font-medium">{mood.label}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Energy & Social Sliders */}
              <Card className="glass border-white/10">
                <CardHeader>
                  <CardTitle>Fine-tune your vibe</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Energy Level */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <EnergyIcon className="h-5 w-5 text-primary" />
                        Energy Level
                      </Label>
                      <Badge variant="secondary" className="text-sm">{energyInfo.label}</Badge>
                    </div>
                    <Slider
                      value={[energyLevel]}
                      onValueChange={(value) => setEnergyLevel(value[0])}
                      min={0}
                      max={100}
                      step={1}
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>🌙 Low Key</span>
                      <span>⚡ Maximum</span>
                    </div>
                  </div>

                  {/* Social Preference */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        Social Vibe
                      </Label>
                      <Badge variant="secondary" className="text-sm">{getSocialLabel(socialVibe)}</Badge>
                    </div>
                    <Slider
                      value={[socialVibe]}
                      onValueChange={(value) => setSocialVibe(value[0])}
                      min={0}
                      max={100}
                      step={1}
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>🧘 Solo Vibes</span>
                      <span>🎊 Party Animal</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Music Taste */}
              <Card className="glass border-white/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Music className="h-5 w-5 text-primary" />
                    What's your sound?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {genres.map((genre) => (
                      <Badge
                        key={genre}
                        variant={selectedGenres.includes(genre) ? 'default' : 'outline'}
                        className={`cursor-pointer text-sm py-2 px-4 transition-all ${
                          selectedGenres.includes(genre) 
                            ? 'bg-primary hover:bg-primary/80' 
                            : 'hover:bg-white/10'
                        }`}
                        onClick={() => handleGenreToggle(genre)}
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button
                onClick={handleSubmitVibe}
                disabled={isSubmitting || !selectedMood}
                className="w-full h-14 text-lg bg-gradient-to-r from-primary via-purple-500 to-secondary hover:opacity-90 shadow-glow"
                size="lg"
              >
                {isSubmitting ? (
                  <>
                    <Sparkles className="h-5 w-5 mr-2 animate-spin" />
                    Analyzing Your Vibe...
                  </>
                ) : (
                  <>
                    <Zap className="h-5 w-5 mr-2" />
                    Get My Vibe Score
                  </>
                )}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Matches Tab */}
        <TabsContent value="matches" className="space-y-6 mt-6">
          {matches.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {matches.map((match) => (
                <MatchCard key={match.match_id} match={match} onRespond={handleMatchResponse} />
              ))}
            </div>
          ) : (
            <Card className="glass border-white/10 p-12 text-center">
              <Users className="h-20 w-20 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold mb-2">No matches yet</h3>
              <p className="text-gray-400 mb-6">
                Complete a vibe check to find people who match your energy!
              </p>
              <Button onClick={() => setActiveTab('check')} className="shadow-glow">
                <Zap className="h-4 w-4 mr-2" />
                Start Vibe Check
              </Button>
            </Card>
          )}
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Target} label="Total Checks" value={vibeStats.total_checks} />
            <StatCard icon={TrendingUp} label="Avg Score" value={vibeStats.average_score} suffix="pts" />
            <StatCard icon={Trophy} label="Highest" value={vibeStats.highest_score} suffix="pts" />
            <StatCard icon={Flame} label="Streak" value={vibeStats.current_streak} suffix="days" />
          </div>

          {/* Vibe History */}
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle>Recent Vibe Checks</CardTitle>
            </CardHeader>
            <CardContent>
              {vibeHistory.length > 0 ? (
                <div className="space-y-3">
                  {vibeHistory.slice(0, 5).map((check) => (
                    <div key={check.vibe_id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          check.score >= 70 ? 'bg-green-500/20' : check.score >= 50 ? 'bg-yellow-500/20' : 'bg-red-500/20'
                        }`}>
                          <span className="font-bold">{check.score}</span>
                        </div>
                        <div>
                          <p className="font-medium capitalize">{check.mood?.replace('_', ' ') || 'Vibe Check'}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(check.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Sparkles className={`h-5 w-5 ${getScoreColor(check.score)}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">No vibe checks yet. Start one now!</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {VIBE_BADGES.map((badge) => {
              const Icon = badge.icon;
              const isUnlocked = badge.id === 'first_check' && vibeStats.total_checks > 0;
              return (
                <Card 
                  key={badge.id} 
                  className={`glass border-white/10 ${isUnlocked ? 'border-primary/50' : 'opacity-60'}`}
                >
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className={`h-14 w-14 rounded-full flex items-center justify-center ${
                      isUnlocked ? 'bg-primary/20' : 'bg-gray-800'
                    }`}>
                      <Icon className={`h-7 w-7 ${isUnlocked ? 'text-primary' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-bold">{badge.name}</h3>
                      <p className="text-sm text-gray-400">{badge.requirement}</p>
                    </div>
                    {isUnlocked && (
                      <Badge className="bg-primary">Unlocked</Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, suffix }: { icon: any; label: string; value: number; suffix?: string }) {
  return (
    <Card className="glass border-white/10">
      <CardContent className="p-4 text-center">
        <Icon className="h-6 w-6 text-primary mx-auto mb-2" />
        <div className="text-2xl font-bold">
          {value}{suffix && <span className="text-sm text-gray-400 ml-1">{suffix}</span>}
        </div>
        <p className="text-xs text-gray-400">{label}</p>
      </CardContent>
    </Card>
  );
}

// Enhanced Match Card Component
function MatchCard({
  match,
  onRespond,
}: {
  match: VibeMatch;
  onRespond: (matchId: string, status: 'accepted' | 'rejected') => void;
}) {
  return (
    <Card className="glass border-white/10 overflow-hidden hover:border-primary/50 transition-all group">
      <CardContent className="p-6 space-y-4">
        {/* User Info */}
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary to-secondary p-0.5">
              <div className="h-full w-full rounded-full bg-background flex items-center justify-center overflow-hidden">
                {match.matched_user_picture ? (
                  <img
                    src={match.matched_user_picture}
                    alt={match.matched_user_name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User className="h-8 w-8 text-primary" />
                )}
              </div>
            </div>
            {match.similarity_score >= 80 && (
              <div className="absolute -bottom-1 -right-1 h-6 w-6 bg-yellow-500 rounded-full flex items-center justify-center">
                <Star className="h-4 w-4 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg">{match.matched_user_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {match.similarity_score}% Match
              </span>
              {match.similarity_score >= 90 && (
                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-xs">Perfect Match!</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Match Score Bar */}
        <div className="space-y-2">
          <Progress value={match.similarity_score} className="h-2" />
          <p className="text-xs text-gray-400 text-center">
            {match.similarity_score >= 80 ? '🔥 Amazing compatibility!' : 
             match.similarity_score >= 60 ? '✨ Great potential!' : '👋 Worth connecting!'}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={() => onRespond(match.match_id, 'rejected')}
            variant="outline"
            className="flex-1 border-white/20 hover:bg-red-500/20 hover:border-red-500/50"
          >
            <X className="h-4 w-4 mr-2" />
            Pass
          </Button>
          <Button
            onClick={() => onRespond(match.match_id, 'accepted')}
            className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90 shadow-glow"
          >
            <Heart className="h-4 w-4 mr-2" />
            Connect
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
